import { useState } from 'react';
import {
  BookOpen,
  Star,
  Languages
} from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';

import VocabQuizMode from './VocabQuizMode';
import VocabReviewMode from './VocabReviewMode';

/**
 * Vocabulary Flash Cards - Mode Selector
 *
 * Provides two modes:
 * - Quiz Mode: Multiple choice questions with mastery tracking
 * - Review Mode: Flip-card review for mastered words
 *
 * Also includes a kanji toggle (session-based, off by default).
 */
const FlashCardGame = ({ studentId, token }) => {
  const [mode, setMode] = useState('quiz'); // 'quiz' or 'review'
  const [showKanji, setShowKanji] = useState(false);
  const [category, setCategory] = useState('');

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
  };

  return (
    <div className="space-y-4">
      {/* Mode Selector Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMode('quiz')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  mode === 'quiz'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <BookOpen className="h-4 w-4" />
                Quiz Mode
              </button>
              <button
                onClick={() => setMode('review')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  mode === 'review'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Star className="h-4 w-4" />
                Review Mastered
              </button>
            </div>

            {/* Kanji Toggle */}
            <div className="flex items-center gap-3">
              <Languages className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <label className="text-sm text-gray-600 dark:text-gray-400">Show Kanji</label>
              <Switch
                checked={showKanji}
                onCheckedChange={setShowKanji}
              />
            </div>
          </div>

          {/* Mode Description */}
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {mode === 'quiz' ? (
              <p>
                Answer multiple choice questions. Get 3 correct on a word to master it!
                Complete all words at your level to unlock the next difficulty.
              </p>
            ) : (
              <p>
                Review your mastered words using flip cards.
                Mastered words have 3+ correct answers in quiz mode.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active Mode Component */}
      {mode === 'quiz' ? (
        <VocabQuizMode
          studentId={studentId}
          token={token}
          showKanji={showKanji}
          category={category}
          onCategoryChange={handleCategoryChange}
        />
      ) : (
        <VocabReviewMode
          studentId={studentId}
          token={token}
          showKanji={showKanji}
          category={category}
          onCategoryChange={handleCategoryChange}
        />
      )}
    </div>
  );
};

export default FlashCardGame;
